export type ValidationIssueFieldTarget = {
  party_index: number;
  field: string;
};

export type MultipartyValidationIssue = {
  type: string;
  severity: string;
  field?: string;
  // C2: structured field targets with party index per field
  field_targets?: ValidationIssueFieldTarget[];
  party_indices: number[];
  message: string;
  // C2: prompt shown inline next to Continue on "confirm" severity
  confirm_prompt?: string;
};

export type Step4ViewMode = "collect" | "correct" | "approve";

const COMPANY_LETTER_BY_SLOT_INDEX = ["A", "B", "C"] as const;

export function isDraftBlockingIssue(issue: MultipartyValidationIssue): boolean {
  // C2 canonical severity is "block"; legacy severities are "blocking" / "error"
  return (
    issue.severity === "block" ||
    issue.severity === "blocking" ||
    issue.severity === "error"
  );
}

export function hasDraftBlockingIssues(
  issues?: MultipartyValidationIssue[] | null,
): boolean {
  return Array.isArray(issues) && issues.some(isDraftBlockingIssue);
}

export function normalizeValidationIssueField(field?: string): string | null {
  const normalized = (field || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (!normalized) return null;
  if (normalized === "registration") return "registrationnumber";
  return normalized;
}

function validationIssueLabelForField(fieldKey: string): string {
  switch (fieldKey) {
    case "registrationnumber":
      return "registration";
    case "jurisdiction":
      return "jurisdiction";
    case "type":
      return "type";
    case "name":
      return "name";
    case "kyc":
      return "kyc";
    case "address":
    default:
      return "address";
  }
}

function addFieldTargetHighlights(
  issue: MultipartyValidationIssue,
  warningFieldNames: Set<string>,
  warningFieldKeysBySlot: Set<string>,
): void {
  // C2 structured field_targets take priority over legacy field+party_indices
  if (Array.isArray(issue.field_targets) && issue.field_targets.length > 0) {
    for (const target of issue.field_targets) {
      const fieldKey = normalizeValidationIssueField(target.field);
      if (!fieldKey) continue;
      const letter = COMPANY_LETTER_BY_SLOT_INDEX[target.party_index];
      if (!letter) continue;
      const fieldLabel = validationIssueLabelForField(fieldKey);
      warningFieldNames.add(`company ${letter.toLowerCase()} ${fieldLabel}`);
      warningFieldKeysBySlot.add(`${target.party_index}:${fieldKey}`);
    }
    return;
  }

  // Legacy: flat field + party_indices
  const fieldKey = normalizeValidationIssueField(issue.field);
  if (!fieldKey) return;
  const fieldLabel = validationIssueLabelForField(fieldKey);
  for (const partyIndex of issue.party_indices || []) {
    const letter = COMPANY_LETTER_BY_SLOT_INDEX[partyIndex];
    if (!letter) continue;
    warningFieldNames.add(`company ${letter.toLowerCase()} ${fieldLabel}`);
    warningFieldKeysBySlot.add(`${partyIndex}:${fieldKey}`);

    if (issue.type === "address_country_mismatch" && fieldKey === "address") {
      warningFieldNames.add(`company ${letter.toLowerCase()} jurisdiction`);
      warningFieldKeysBySlot.add(`${partyIndex}:jurisdiction`);
    }
  }
}

export function buildValidationIssueLookup(
  issues?: MultipartyValidationIssue[] | null,
): {
  issues: MultipartyValidationIssue[];
  warningFieldNames: Set<string>;
  warningFieldKeysBySlot: Set<string>;
  blockingIssues: MultipartyValidationIssue[];
} {
  const normalizedIssues = Array.isArray(issues) ? issues : [];
  const warningFieldNames = new Set<string>();
  const warningFieldKeysBySlot = new Set<string>();
  const blockingIssues = normalizedIssues.filter(isDraftBlockingIssue);

  for (const issue of normalizedIssues) {
    // Highlight fields for block, warning, AND confirm issues (C2: all non-informational severities)
    if (
      !isDraftBlockingIssue(issue) &&
      issue.severity !== "warning" &&
      issue.severity !== "confirm"
    ) {
      continue;
    }
    addFieldTargetHighlights(issue, warningFieldNames, warningFieldKeysBySlot);
  }

  return {
    issues: normalizedIssues,
    warningFieldNames,
    warningFieldKeysBySlot,
    blockingIssues,
  };
}

export function resolveHasPartySlotData(args: {
  currentStep?: number;
  partiesStepComplete: boolean;
  hasPartySlots: boolean;
  isDownloadStep: boolean;
  viewMode?: Step4ViewMode | null;
  validationIssues?: MultipartyValidationIssue[] | null;
}): boolean {
  const {
    currentStep,
    partiesStepComplete,
    hasPartySlots,
    isDownloadStep,
    viewMode,
    validationIssues,
  } = args;

  if (isDownloadStep || currentStep !== 4 || !hasPartySlots) {
    return false;
  }

  // Approve mode uses GroupedPartyApprovalView (pencil on every row), not the slot composer.
  if (viewMode === "approve") {
    return false;
  }

  if (!partiesStepComplete) {
    return true;
  }

  return viewMode === "correct" || hasDraftBlockingIssues(validationIssues);
}

export function resolveHasPartySummaryData(args: {
  currentStep?: number;
  partiesStepComplete: boolean;
  hasPartySlots: boolean;
  isDownloadStep: boolean;
  viewMode?: Step4ViewMode | null;
  partyFieldCorrectionActive: boolean;
}): boolean {
  const {
    currentStep,
    partiesStepComplete,
    hasPartySlots,
    isDownloadStep,
    viewMode,
    partyFieldCorrectionActive,
  } = args;

  if (isDownloadStep || currentStep !== 4 || !hasPartySlots) {
    return false;
  }

  if (partyFieldCorrectionActive || viewMode === "correct") {
    return false;
  }

  return partiesStepComplete || viewMode === "approve";
}

export function shouldSuppressStaleStep4Fallback(args: {
  hasPartySlots: boolean;
  viewMode?: Step4ViewMode | null;
}): boolean {
  return args.hasPartySlots && args.viewMode !== "collect";
}

export function shouldSuppressStep4GreetingMarkdown(args: {
  hasPartySlotData: boolean;
  isLatestMessage: boolean;
}): boolean {
  return args.hasPartySlotData && args.isLatestMessage;
}
